import { createRouter, createWebHistory } from 'vue-router';
import { hasDictionary, loadDictionary } from './i18n';

import SessionTracker from "@/core/session";

import SwaggerService from "@/services/swagger.service";

import { Helpers } from "@/core/utils";

export function setupRouter( store, i18n, version, permission )
{
    if( ['production','test'].includes( import.meta.env.MODE ) )
    {
        SessionTracker.init( { rootUrl: import.meta.env.VITE_APP_TRACKER_API_URL, paths: { record: '/recording/checkpoint', tracker: '/track', feedback: '/feedback' } , timeout: { read: 30000, write: 5000 } } );
    }

    const routes =
    [
        {
            path      : '/',
            name      : 'Root',
            component : () => import('@/templates/layouts/Layout.vue'),
            redirect  : '/dashboard',
            children  :
            [
                //OVERVIEW
                {
                    path        : '/dashboard',
                    name        : 'Dashboard',
                    meta        :
                    {
                        section : 'default',
                        icon    : 'isax isax-element-45',
                        title   : 'router.home',

                        requiresAuth: true,
                        navigation  : true,
                        permissions : [ 'admin', 'employer', 'recruiter' ]
                    },
                    children    :
                    [
                        {
                            path        : '/dashboard',
                            name        : 'DashboardOverview',
                            meta        : { requiresAuth: true },
                            component   : () => import('@/templates/views/dashboard/overview/detail/Detail.vue')
                        }
                    ]
                },

                //MY PROFILE
                {
                    path        : '/my-profile',
                    name        : 'MyProfile',
                    meta        :
                    {
                        section : 'default',
                        icon    : 'isax isax-tag-user5',
                        title   : 'router.myAccount',

                        requiresAuth: true,
                        navigation  : true,
                        permissions : [ 'admin', 'employer', 'recruiter' ]
                    },
                    beforeEnter : ( to, from ) => { return '/accounts/'+ store.getters['auth/user/detail']?.currentAccount.id; },
                },

                //COMPANY PROFILE
                {
                    path        : '/company-profile',
                    name        : 'CompanyProfile',
                    meta        :
                    {
                        section : 'default',
                        key     : 'pending_onboardings',
                        icon    : 'isax isax-building5',
                        title   : 'router.companyProfile',

                        requiresAuth: true,
                        navigation  : true,
                        permissions : [ 'admin', 'employer', 'recruiter' ],
                    },
                    beforeEnter : ( to, from ) =>
                    {
                        let currentOrg = store.getters['auth/user/detail']?.currentAccount.organization;

                        return `/${ currentOrg?.flag === 'agency' ? 'agencies' : 'employers' }/${currentOrg.id}`;
                    }
                },

                //INBOX
                {
                    path        : '/messages',
                    name        : 'Mailbox',
                    meta        :
                    {

                        section : 'default',
                        key     : 'unread_conversations',
                        icon    : 'isax isax-sms5',
                        title   : 'router.mailbox',

                        requiresAuth      : true,
                        navigation        : true,
                        disableLayoutShift: true,
                        permissions       : [ 'admin', 'employer', 'recruiter' ]
                    },
                    children    :
                    [
                        {
                            path        : '/messages',
                            name        : 'MessagesList',
                            meta        : { requiresAuth: true },
                            component   : () => import('@/templates/views/messages/mailbox/list/List.vue'),
                            children    :
                            [
                                {
                                    path        : '/messages/:conversationID',
                                    name        : 'MessageDetail',
                                    meta        : { requiresAuth: true },
                                    props       : ( route ) => { return { ...route.params, conversationID: route.params.conversationID } },
                                    component   : () => import('@/templates/views/messages/mailbox/list/blocks/InboxDetail.vue')
                                }
                            ]
                        }
                    ]
                },

                //LIVECHAT
                {
                    path        : '/livechat',
                    name        : 'Livechat',
                    meta        :
                    {
                        section : 'default',
                        key     : 'unread_livechat_conversations',
                        icon    : 'isax isax-microphone-25',
                        title   : 'router.liveChat',

                        requiresAuth      : true,
                        navigation        : true,
                        disableLayoutShift: true,
                        permissions       : [ 'admin' ]
                    },
                    children    :
                    [
                        {
                            path        : '/livechat',
                            name        : 'LivechatList',
                            meta        : { requiresAuth: true },
                            component   : () => import('@/templates/views/livechat/mailbox/list/List.vue'),
                            children    :
                            [
                                {
                                    path        : '/livechat/:threadID',
                                    name        : 'LivechatDetail',
                                    meta        : { requiresAuth: true },
                                    props       : ( route ) => { return { ...route.params, threadID: route.params.threadID } },
                                    component   : () => import('@/templates/views/livechat/mailbox/list/blocks/InboxDetail.vue')
                                }
                            ]
                        }
                    ]
                },
                
                /*
                //TASKS
                {
                    path        : '/tasks',
                    name        : 'Tasks',
                    meta        :
                    {
                        section : 'main',
                        icon    : 'isax isax-calendar5',
                        title   : i18n.global.t( 'router.tasks' ),

                        requiresAuth: true,
                        navigation  : true,
                    },
                    redirect    : '/tasks/dashboard',
                    children    :
                    [
                        {
                            path        : '/tasks/dashboard',
                            name        : 'TasksOverview',
                            meta        : { requiresAuth: true },
                            component   : () => import('@/templates/views/tasks/overview/detail/Detail.vue')
                        }
                    ]
                },
               */

                //HIDDEN => EMPLOYMENT => ACCOUNTS
                {
                    path        : '/accounts',
                    name        : 'Accounts',
                    meta        :
                    {
                        section : 'recruitment',
                        icon    : 'fa-users-tie',
                        title   : i18n.global.t( 'router.accounts' ),

                        requiresAuth: true,
                        navigation  : false,
                        permissions : [ 'admin', 'employer', 'recruiter' ],
                    },
                    children    :
                    [
                        {
                            path        : '/accounts',
                            name        : 'AccountsList',
                            meta        :
                            {
                                requiresAuth: true
                            },
                            component   : () => import('@/templates/views/recruitment/accounts/list/List.vue')
                        },
                        {
                            path        : '/accounts/:accountID',
                            name        : 'AccountDetail',
                            meta        : { requiresAuth: true },
                            props       : ( route ) => { return { ...route.params, accountID: route.params.accountID } },
                            component   : () => import('@/templates/views/recruitment/accounts/detail/Detail.vue'),
                        }
                    ]
                },

                //EMPLOYMENT => JOBS
                {
                    path        : '/jobs',
                    name        : 'Jobs',
                    meta        :
                    {
                        section : 'recruitment',
                        icon    : 'isax isax-briefcase5',
                        title   : i18n.global.t( 'router.jobRequisition' ),

                        requiresAuth: true,
                        navigation  : true,
                        permissions : [ 'admin', 'employer', 'recruiter' ]
                    },
                    children    :
                    [
                        {
                            path        : '/jobs',
                            name        : 'JobsList',
                            meta        : { requiresAuth: true },
                            component   : () => import('@/templates/views/recruitment/jobs/list/List.vue')
                        },
                        {
                            path        : '/jobs/:jobID',
                            name        : 'JobDetail',
                            meta        : { requiresAuth: true },
                            props       : ( route ) => { return { ...route.params, jobID: route.params.jobID } },
                            component   : () => import('@/templates/views/recruitment/jobs/detail/Detail.vue'),
                            children    :
                            [
                                {
                                    path        : '/jobs/:jobID/:conversationID',
                                    name        : 'JobMessageDetail',
                                    meta        : { requiresAuth: true, disableLayoutShift: true },
                                    props       : ( route ) => { return { ...route.params, jobID: route.params.jobID, conversationID: route.params.conversationID } },
                                    component   : () => import('@/templates/views/messages/mailbox/detail/Detail.vue')
                                }
                            ]
                        }
                    ]
                },

                 //EMPLOYMENT => INVITATIONS
                 {
                    path        : '/invitations',
                    name        : 'Invitations',
                    meta        :
                    {
                        section : 'recruitment',
                        icon    : 'isax isax-archive-tick5',
                        title   : i18n.global.t( 'router.invitations' ),

                        requiresAuth: true,
                        navigation  : true,
                        permissions : [ 'admin', 'employer', 'recruiter' ]
                    },
                    children    :
                    [
                        {
                            path        : '/invitations',
                            name        : 'InvitationsList',
                            meta        : { requiresAuth: true },
                            component   : () => import('@/templates/views/recruitment/invitations/list/List.vue')
                        }
                    ]
                },

                //EMPLOYMENT => APPLICATIONS
                {
                    path        : '/applications',
                    name        : 'Applications',
                    meta        :
                    {
                        section : 'recruitment',
                        icon    : 'isax isax-personalcard5',
                        title   : i18n.global.t( 'router.applications' ),

                        requiresAuth: true,
                        navigation  : true,
                        permissions  : [ 'admin', 'employer', 'recruiter' ]
                    },
                    children    :
                    [
                        {
                            path        : '/applications',
                            name        : 'ApplicationsList',
                            meta        : { requiresAuth: true },
                            component   : () => import('@/templates/views/recruitment/applications/list/List.vue'),
                        },
                        {
                            path        : '/applications/:applicationID',
                            name        : 'ApplicationDetail',
                            meta        : { requiresAuth: true },
                            props       : ( route ) => { return { ...route.params, applicationID: route.params.applicationID } },
                            component   : () => import('@/templates/views/recruitment/applications/detail/Detail.vue'),
                            children    :
                            [
                                {
                                    path        : '/applications/:applicationID/:conversationID',
                                    name        : 'ApplicationMessageDetail',
                                    meta        : { requiresAuth: true, disableLayoutShift: true },
                                    props       : ( route ) => { return { ...route.params, applicationID: route.params.applicationID, conversationID: route.params.conversationID } },
                                    component   : () => import('@/templates/views/messages/mailbox/detail/Detail.vue')
                                }
                            ]
                        }
                    ]
                },

                //EMPLOYMENT => PLACEMENTS
                {
                    path        : '/placements',
                    name        : 'Placements',
                    meta        :
                    {
                        section : 'recruitment',
                        icon    : 'isax isax-cd5',
                        title   : i18n.global.t( 'router.placements' ),

                        requiresAuth: true,
                        navigation  : true,
                        permissions : [ 'admin', 'employer', 'recruiter' ]
                    },
                    children    :
                    [
                        {
                            path        : '/placements',
                            name        : 'PlacementsList',
                            meta        : { requiresAuth: true },
                            component   : () => import('@/templates/views/recruitment/placements/list/List.vue')
                        },
                        {
                            path        : '/placements/:placementID',
                            name        : 'PlacementDetail',
                            meta        : { requiresAuth: true },
                            props       : ( route ) => { return { ...route.params, placementID: route.params.placementID } },
                            component   : () => import('@/templates/views/recruitment/placements/detail/Detail.vue'),
                            children    :
                            [
                                {
                                    path        : '/placements/:placementID/:conversationID',
                                    name        : 'PlacementMessageDetail',
                                    meta        : { requiresAuth: true, disableLayoutShift: true },
                                    props       : ( route ) => { return { ...route.params, placementID: route.params.placementID, conversationID: route.params.conversationID } },
                                    component   : () => import('@/templates/views/messages/mailbox/detail/Detail.vue')
                                }
                            ]
                        }
                    ]
                },

                //EMPLOYMENT => WORKERS = CONTRACTOR USER
                {
                    path        : '/workers',
                    name        : 'Workers',
                    meta        :
                    {
                        section : 'recruitment',
                        icon    : 'isax isax-tag-user5',
                        title   : 'Workers',

                        requiresAuth: true,
                        navigation  : true,
                        permissions : [ 'admin', 'employer', 'recruiter' ],
                    },
                    children    :
                    [
                        {
                            path        : '/workers',
                            name        : 'WorkersList',
                            meta        : { requiresAuth: true },
                            component   : () => import('@/templates/views/recruitment/workers/list/List.vue')
                        },
                        {
                            path        : '/worker/:workerID',
                            name        : 'WorkerDetail',
                            meta        : { requiresAuth: true },
                            props       : ( route ) => { return { ...route.params, workerID: route.params.workerID } },
                            component   : () => import('@/templates/views/recruitment/workers/detail/Detail.vue'),
                        }
                    ]
                },

                //EMPLOYMENT => CONTRACTS
                {
                    path        : '/contracts',
                    name        : 'Contractors',
                    meta        :
                    {
                        section : 'recruitment',
                        icon    : 'isax isax-document-text5',
                        title   : i18n.global.t( 'router.contractors' ),

                        requiresAuth: true,
                        navigation  : true,
                        permissions : [ 'admin', 'employer', 'recruiter' ]
                    },
                    children    :
                    [
                        {
                            path        : '/contracts',
                            name        : 'ContractsList',
                            meta        : { requiresAuth: true },
                            component   : () => import('@/templates/views/recruitment/contracts/list/List.vue')
                        },
                        {
                            path        : '/contracts/:contractID',
                            name        : 'ContractDetail',
                            meta        : { requiresAuth: true },
                            props       : ( route ) => { return { ...route.params, contractID: route.params.contractID } },
                            component   : () => import('@/templates/views/recruitment/contracts/detail/Detail.vue'),
                            children    :
                            [
                                {
                                    path        : '/contracts/:contractID/:conversationID',
                                    name        : 'ContractMessageDetail',
                                    meta        : { requiresAuth: true, disableLayoutShift: true },
                                    props       : ( route ) => { return { ...route.params, contractID: route.params.contractID, conversationID: route.params.conversationID } },
                                    component   : () => import('@/templates/views/messages/mailbox/detail/Detail.vue')
                                }
                            ]
                        }
                    ]
                },

                //EMPLOYMENT => AGENCIES
                {
                    path        : '/agencies',
                    name        : 'Agencies',
                    meta        :
                    {
                        section : 'recruitment',
                        icon    : 'isax isax-buliding5',
                        title   : i18n.global.t( 'router.agencies' ),

                        requiresAuth: true,
                        navigation  : true,
                        permissions : [ 'admin', 'employer', 'recruiter' ]
                    },
                    children    :
                    [
                        {
                            path        : '/agencies',
                            name        : 'AgenciesList',
                            meta        : { requiresAuth: true, permissions: [ 'admin', 'employer' ] },
                            component   : () => import('@/templates/views/recruitment/agencies/list/List.vue')
                        },
                        {
                            path        : '/agencies/:agencyID',
                            name        : 'AgencyDetail',
                            meta        : { requiresAuth: true, permissions: [ 'admin', 'employer', 'recruiter' ] },
                            props       : (route) => { return { ...route.params, agencyID: route.params.agencyID } },
                            component   : () => import('@/templates/views/recruitment/agencies/detail/Detail.vue'),
                            children    :
                            [
                                {
                                    path        : '/agencies/:agencyID/:conversationID',
                                    name        : 'AgencyMessageDetail',
                                    meta        : { requiresAuth: true, disableLayoutShift: true },
                                    props       : ( route ) => { return { ...route.params, agencyID: route.params.agencyID, conversationID: route.params.conversationID } },
                                    component   : () => import('@/templates/views/messages/mailbox/list/blocks/InboxDetail.vue')
                                }
                            ]
                        }
                    ]
                },

                //EMPLOYMENT => EMPLOYERS
                {
                    path        : '/employers',
                    name        : 'Employers',
                    meta        :
                    {
                        section : 'recruitment',
                        icon    : 'isax isax-buildings5',
                        title   : i18n.global.t( 'router.employers' ),

                        requiresAuth: true,
                        navigation  : true,
                        permissions : [ 'admin' ]
                    },
                    children    :
                    [
                        {
                            path        : '/employers',
                            name        : 'EmployersList',
                            meta        : { requiresAuth: true, permissions: [ 'admin' ] },
                            component   : () => import('@/templates/views/recruitment/employers/list/List.vue')
                        },
                        {
                            path        : '/employers/:agencyID',
                            name        : 'EmployerDetail',
                            meta        : { requiresAuth: true, permissions: [ 'admin', 'employer' ] },
                            props       : (route) => { return { ...route.params, agencyID: route.params.agencyID } },
                            component   : () => import('@/templates/views/recruitment/agencies/detail/Detail.vue'),
                            children    :
                            [
                                {
                                    path        : '/employers/:agencyID/:conversationID',
                                    name        : 'EmployerMessageDetail',
                                    meta        : { requiresAuth: true, disableLayoutShift: true },
                                    props       : ( route ) => { return { ...route.params, agencyID: route.params.agencyID, conversationID: route.params.conversationID } },
                                    component   : () => import('@/templates/views/messages/mailbox/list/blocks/InboxDetail.vue')
                                }
                            ]
                        }
                    ]
                },

                 //EMPLOYMENT => PROGRAMMES
                 {
                    path        : '/programmes',
                    name        : 'Programs',
                    meta        :
                    {
                        section : 'recruitment',
                        icon    : 'isax isax-dcube5',
                        title   : i18n.global.t( 'router.programs' ),

                        requiresAuth: true,
                        navigation  : true,
                        permissions : [ 'admin' ]
                    },
                    children    :
                    [
                        {
                            path        : '/programmes',
                            name        : 'ProgrammesList',
                            meta        : { requiresAuth: true },
                            component   : () => import('@/templates/views/recruitment/programmes/list/List.vue'),
                        },
                        {
                            path        : '/programmes/:programmeID',
                            name        : 'ProgrammeDetail',
                            meta        : { requiresAuth: true, queryKeys: ['programmeLocationID'] },
                            props       : ( route ) => { return { ...route.params, programmeID: route.params.programmeID } },
                            component   : () => import('@/templates/views/recruitment/programmes/detail/Detail.vue'),
                        }
                    ]
                },

                //TOOLS => ANALYTICS
                {
                    path        : '/analytics',
                    name        : 'Analytics',
                    meta        :
                    {
                        section : 'tool',
                        icon    : 'isax isax-chart-35',
                        title   : i18n.global.t( 'router.analytics' ),

                        requiresAuth: true,
                        navigation  : true,
                        permissions : [ 'admin', 'employer' ],
                    },
                    redirect    : '/analytics/administrator',
                    children    :
                    [
                        {
                            path        : 'administrator',
                            name        : 'administratorAnalyticsOverview',
                            props       : ( route ) => { return { role: 'administrator', version: 1  } },
                            meta        : { requiresAuth: true },
                            component   : () => import('@/templates/views/dashboard/analytics/detail/Detail.vue')
                        }
                    ]
                },

                {
                    path        : '/supplier-performance',
                    name        : 'SupplierPerformance',
                    meta        :
                    {
                        section : 'tool',
                        icon    : 'isax isax-status-up5',
                        title   : 'Supplier performance',

                        requiresAuth : true,
                        navigation   : true,
                        permissions  : [ 'admin' ],
                    },
                    children:
                    [
                        {
                            path        : '/supplier-performance',
                            name        : 'SupplierPerformanceList',
                            meta        : { requiresAuth: true },
                            component   : () => import('@/templates/views/dashboard/supplierPerformance/list/List.vue')
                        }
                    ]
                },

                //TOOLS => JOB TRACKER
                {
                    path        : '/job-tracker',
                    name        : 'JobTracker',
                    meta        :
                    {
                        section : 'tool',
                        icon    : 'isax isax-presention-chart5',
                        title   : 'router.jobTracker',

                        requiresAuth: true,
                        navigation  : true,
                        permissions : [ 'admin' ]
                    },
                    component   : () => import('@/templates/views/dashboard/jobtracker/detail/Detail.vue')
                },

                {
                    path        : '/talent-intelligence',
                    name        : 'TalentIntelligence',
                    meta        :
                    {
                        section : 'tool',
                        icon    : 'isax isax-airdrop5',
                        title   : 'router.talentIntelligence',

                        requiresAuth: true,
                        navigation  : true,
                        permissions : [ 'admin', 'employer' ],
                    },
                    beforeEnter : async ( to, from ) =>
                    {
                        window.open( 'https://ti.ramp.global/', '_blank');

                        if( from.name )
                        {
                            return false;
                        }
                        else
                        {
                            return { path: '/dashboard' };
                        }
                    }
                },

                //TOOLS => TALENT INTELLIGENCE
                {
                    path        : '/insights-map',
                    name        : 'InsightsMap',
                    meta        :
                    {
                        section : 'tool',
                        icon    : 'isax isax-global-search5',
                        title   : 'router.talentIntelligenceMap',

                        requiresAuth: true,
                        navigation  : true,
                        permissions : [ 'admin' ],
                    },
                    component   : () => import('@/templates/views/dashboard/insights/map/Map.vue')
                },

                //TOOLS => ADMIN PANEL
                {
                    path        : '/admin',
                    name        : 'Admin',
                    meta        :
                    {
                        section : 'tool',
                        icon    : 'isax isax-setting-25',
                        title   : 'router.admin',

                        requiresAuth: true,
                        navigation  : true,
                        permissions : [ 'admin' ],
                    },
                    beforeEnter : async ( to, from ) =>
                    {
                        window.open( import.meta.env.VITE_APP_ADMIN_URL, '_blank');

                        if( from.name )
                        {
                            return false;
                        }
                        else
                        {
                            return { path: '/dashboard' };
                        }
                    }
                },

                //SETTINGS => PROFILE
                {
                    path        : '/profile',
                    name        : 'Profile',
                    meta        :
                    {
                        title   : 'router.settings',

                        requiresAuth: true,
                        navigation  : false
                    },
                    children    :
                    [
                        {
                            path        : '/profile',
                            name        : 'ProfileDetail',
                            meta        : { requiresAuth: true, disableLayoutShift: true },
                            component   : () => import('@/templates/views/profile/detail/Detail.vue'),
                            children    :
                            [
                                {
                                    path        : '/profile/company/:sectionID',
                                    name        : 'ProfileCompanyDetail',
                                    meta        : { requiresAuth: true },
                                    props       : ( route ) => { return { ...route.params, sectionID: route.params.sectionID } },
                                    component   : () => import('@/templates/views/profile/detail/blocks/company/list/blocks/Detail.vue')
                                },
                                {
                                    path        : ':sectionID',
                                    name        : 'ProfileAccountDetail',
                                    meta        : { requiresAuth: true },
                                    props       : ( route ) => { return { ...route.params, sectionID: route.params.sectionID } },
                                    component   : () => import('@/templates/views/profile/detail/blocks/profile/list/blocks/Detail.vue')
                                }
                            ]
                        }
                    ]
                },

                /*
                {
                    path        : '/insights-calc',
                    name        : 'InsightsCalc',
                    meta        : { requiresAuth: true, navigation: false, permissions: [ 'admin', 'employer', 'recruiter' ], section: 'tool', title: 'router.insightsCalc', icon: 'isax isax-calculator5' },
                    component   : () => import('@/templates/views/dashboard/insights/detail/Detail.vue')
                },
                {
                    path        : '/insights-ai-job',
                    name        : 'InsightsAIJob',
                    meta        : { requiresAuth: true, navigation: true, permissions: [ 'admin', 'employer', 'recruiter' ], section: 'tool', title: 'RAMP Copilot', icon: 'isax isax-magicpen5' },
                    component   : () => import('@/templates/views/dashboard/insights/ai/Job.vue')
                },
                {
                    path        : '/insights-ai-chat',
                    name        : 'InsightsAIChat',
                    meta        : { requiresAuth: true, navigation: true, permissions: [ 'admin', 'employer', 'recruiter' ], section: 'tool', title: 'RAMP Copilot Chat', icon: 'isax isax-magicpen5' },
                    component   : () => import('@/templates/views/dashboard/insights/ai/Chat.vue')
                },
                */
            ]
        },

        {
            path        : '/stripe-session',
            name        : 'StripeSession',
            meta        : { requiresAuth: true, navigation: false },
            component   : () => import('@/templates/views/system/StripeSession.vue'),
            beforeEnter : async ( to, from ) =>
            {
                if( !to.query.hasOwnProperty('session_id') )
                {
                    return { path: '/missing-stripe-session-id', query: { statusCode: 400 } };
                }

                return true;
            }
        },
        {
            path        : '/login',
            name        : 'Login',
            meta        : { requiresAuth : false },
            beforeEnter : async ( to, from ) => { await store.dispatch('auth/user/login'); }
        },
        {
            path        : '/logout',
            name        : 'Logout',
            meta        : { requiresAuth : false },
            //beforeEnter : async ( to, from ) => { await store.dispatch('auth/user/logout'); },
            component   : () => import('@/templates/views/system/Logout.vue')
        },
        {
            path        : '/select-account',
            name        : 'SelectAccount',
            meta        : { requiresAuth : true },
            component   : () => import('@/templates/views/system/SelectAccount.vue')
        },
        {
            path        : '/access-denied',
            name        : 'AccessDenied',
            meta        : { requiresAuth : false },
            props       : ( route ) => { return { statusCode: 403 } },
            component   : () => import('@/templates/views/system/ErrorPage.vue')
        },
        {
            path        : '/:pathMatch(.*)*',
            name        : '404',
            meta        : { requiresAuth: false },
            props       : ( route ) => { return { statusCode: route?.query?.statusCode || 404 } },
            component   : () => import('@/templates/views/system/ErrorPage.vue')
        }
    ]

    const router = createRouter({ history: createWebHistory(), routes });

    router.beforeEach( async ( to, from ) =>
    {
        if( to.query.authToken )
        {
            try
            {
                await store.dispatch( 'auth/user/loginWithToken', { token: to.query.authToken });
            }
            catch( e )
            {
                console.error( 'Login with token failed', e );
            }

            const { authToken, ...newQuery } = to.query;

            await router.replace({ path: to.path, query: newQuery });
        }

        if( !store.getters['auth/user/detail'] )
        {
            await store.dispatch( 'auth/user/get', { requiresAuth: to.meta.requiresAuth, switchAccount: to.query.switchAccount });

            if( to.query.switchAccount )
            {
                delete to.query.switchAccount;

                await router.replace({ path: to.path, query: to.query });
            }
        }

        const savedLocale = Helpers.Storage( undefined, 'get', 'locale' ) || 'en';
        if( !hasDictionary( i18n, savedLocale ) )
        {
            await loadDictionary( i18n, savedLocale );
        }

        if( store.getters['auth/user/detail'] && !store.getters['auth/user/detail'].emailVerified )
        {
            window.location.href = import.meta.env.VITE_APP_AUTH_URL + 'registration/verification?email=' + encodeURIComponent( store.getters['auth/user/detail'].email ) + '&redirect=' + encodeURIComponent( window.location.href );
        }

        if( to.meta.requiresAuth && !store.getters['auth/user/detail']?.currentAccount && to.path !== '/select-account' )
        {
            sessionStorage.setItem( "redirectAfterAccountSelected", window.location.href );

            if( to.query.hasOwnProperty('onboardingProgrammeIDs') )
            {
                sessionStorage.setItem( "onboardingProgrammeIDs", JSON.stringify( Helpers.toArray( to.query.onboardingProgrammeIDs ) ) );
            }

            return { path: '/select-account' };
        }
        else if( to.meta.requiresAuth && store.getters['auth/user/detail']?.currentAccount && to.path === '/select-account' )
        {
            return { path: '/' };
        }

        if( store.getters['auth/user/isLoggedIn'] && to.meta.requiresAuth && to.meta.permissions && !permission.has( to.meta.permissions ) )
        {
            return { path: '/access-denied' };
        }

        if( store.getters['auth/user/detail']?.currentAccount )
        {
            const { currentAccount, id } = store.getters['auth/user/detail'];

            if( ['production','test'].includes( import.meta.env.MODE ) && !( to && from && to.name === from.name && ( to.name.includes('List') || to.name.includes('Detail') ) && ( to.query.cursor === from.query.cursor || to.cursor !== from.query.cursor ) ) )
            {
                SessionTracker.page( to.name + ( to.query.currentTab ? '_' + to.query.currentTab : '' ),
                {
                    url      : ( new URL( to.fullPath, location.href ) ).toString(),
                    referrer : ( new URL( from.fullPath, location.href ) ).toString(),
                    user     : { id: id, accountID: currentAccount.id, organizationID: currentAccount.organization.id }
                });
            }

            if( to.query.hasOwnProperty('onboardingProgrammeIDs') || sessionStorage.getItem('onboardingProgrammeIDs') )
            {
                if( currentAccount.organization.flag === 'agency' )
                {
                    const queryIDs  = Helpers.toArray( to.query.onboardingProgrammeIDs );
                    const storedRaw = sessionStorage.getItem( 'onboardingProgrammeIDs' );
                    const storedIDs = storedRaw ? Helpers.toArray( JSON.parse( storedRaw ) ) : [];
                    const allIDs    = Array.from( new Set([ ...queryIDs, ...storedIDs ]) );

                    await Promise.all( allIDs.map( id => SwaggerService.create_onboarding( id ) ) );
                }

                sessionStorage.removeItem('onboardingProgrammeIDs');

                const { onboardingProgrammeIDs, ...newQuery } = to.query;

                await router.replace({ path: to.path, query: newQuery });

                await router.go( 0 );
            }
        }
    });

    router.afterEach( ( to ) =>
    {
        document.title = ( to.meta.title ? ( to.meta.title.startsWith('router.') ? i18n.global.t( to.meta.title ) : to.meta.title ) : 'Platform' ) + ' | RAMP Global';
    })

    router.onError(( error, to ) =>
    {
        if( error.message.includes('Failed to fetch dynamically imported module') || error.message.includes("Importing a module script failed") )
        {
            version.check( true );
        }
    })

    return router;
}